import { App as AntApp, Button, Card, Descriptions, Empty, Popconfirm, Skeleton, Space, Table, Tag, Typography } from 'antd';
import { ArrowLeftOutlined, PrinterOutlined, RollbackOutlined } from '@ant-design/icons';
import { useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { RoleGate, ADMIN_ROLES, ACCOUNTANT_ROLES } from '@/shared/auth/role';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { usePayment, useReversePayment, useGenerateReceipt, type PaymentStatus } from '../api/payments';
import { presignedDownload } from '@/features/customers/api/customers';
import { formatUzs } from '@/shared/lib/format';
import { asApiError } from '@/shared/lib/api';

const STATUS_COLOR: Record<PaymentStatus, string> = {
  pending: 'gold',
  confirmed: 'green',
  reversed: 'red',
};

export function PaymentDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { message } = AntApp.useApp();
  const { data: payment, isLoading } = usePayment(id);
  const reverse = useReversePayment();
  const receipt = useGenerateReceipt();
  const [reason, setReason] = useState('');

  if (isLoading) return <Skeleton active />;
  if (!payment) return <Empty />;

  async function handleReverse() {
    try {
      await reverse.mutateAsync({ id: payment!.id, reason: reason || 'reversal' });
      void message.success(t('payments.reversed'));
    } catch (err) {
      void message.error(asApiError(err).message);
    }
  }

  async function handleReceipt() {
    try {
      const { key } = await receipt.mutateAsync(payment!.id);
      const url = await presignedDownload(key);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      void message.error(asApiError(err).message);
    }
  }

  return (
    <Space direction="vertical" size={20} style={{ width: '100%' }}>
      <div>
        <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate('/payments')}>
          {t('payments.title')}
        </Button>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: 8 }}>
          <Space size={12} align="baseline">
            <Typography.Title level={2} style={{ margin: 0 }}>{formatUzs(payment.amount)}</Typography.Title>
            <Tag color={STATUS_COLOR[payment.status]}>{t(`payments.status.${payment.status}`)}</Tag>
          </Space>
          <Space>
          {payment.status === 'confirmed' && (
            <RoleGate roles={ACCOUNTANT_ROLES}>
              <Button icon={<PrinterOutlined />} loading={receipt.isPending} onClick={handleReceipt}>
                {t('payments.receipt')}
              </Button>
            </RoleGate>
          )}
          <RoleGate roles={ADMIN_ROLES}>
          {payment.status === 'confirmed' && (
            <Popconfirm
              title={t('payments.reverseConfirmTitle')}
              description={
                <input
                  placeholder={t('payments.reverseReasonPlaceholder')}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  style={{ width: 280, marginTop: 8 }}
                />
              }
              okText={t('common.delete')}
              cancelText={t('common.cancel')}
              okButtonProps={{ danger: true, loading: reverse.isPending }}
              onConfirm={handleReverse}
            >
              <Button danger icon={<RollbackOutlined />}>{t('payments.reverse')}</Button>
            </Popconfirm>
          )}
          </RoleGate>
          </Space>
        </div>
      </div>

      <Card>
        <Descriptions column={{ xs: 1, sm: 2 }} colon={false} labelStyle={{ color: '#64748b' }}>
          <Descriptions.Item label={t('payments.paymentDate')}>{dayjs(payment.paymentDate).format('DD.MM.YYYY')}</Descriptions.Item>
          <Descriptions.Item label={t('payments.method')}>{t(`apartments.paymentMethods.${payment.method}`)}</Descriptions.Item>
          <Descriptions.Item label={t('payments.reference')}>{payment.reference ?? '—'}</Descriptions.Item>
          <Descriptions.Item label={t('payments.contract')}>
            {payment.contract
              ? <Link to={`/contracts/${payment.contract.id}`}>{payment.contract.contractNumber}</Link>
              : '—'}
          </Descriptions.Item>
          {payment.customer && (
            <Descriptions.Item label={t('customers.title')}>
              <Link to={`/customers/${payment.customer.id}`}>
                {payment.customer.lastName} {payment.customer.firstName}
              </Link>
            </Descriptions.Item>
          )}
          {payment.reversedAt && (
            <Descriptions.Item label={t('payments.reversedAt')}>{dayjs(payment.reversedAt).format('DD.MM.YYYY HH:mm')}</Descriptions.Item>
          )}
          {payment.reverseReason && (
            <Descriptions.Item label={t('payments.reverseReason')}>{payment.reverseReason}</Descriptions.Item>
          )}
          {payment.notes && (
            <Descriptions.Item label={t('payments.notes')} span={2}>{payment.notes}</Descriptions.Item>
          )}
        </Descriptions>
      </Card>

      <Card title={t('payments.allocations')} bodyStyle={{ padding: 0 }}>
        <Table
          rowKey="id"
          dataSource={payment.allocations ?? []}
          pagination={false}
          size="small"
          columns={[
            {
              title: t('payments.allocScheduleId'),
              render: (_: unknown, row: NonNullable<typeof payment.allocations>[number]) => {
                if (!row.paymentScheduleId) return t('payments.credit');
                if (row.paymentSchedule?.isDownPayment) return t('contracts.downPaymentRow');
                return row.paymentSchedule
                  ? `${t('contracts.installment')} #${row.paymentSchedule.installmentNumber}`
                  : row.paymentScheduleId.slice(0, 8);
              },
            },
            { title: t('payments.allocAmount'), dataIndex: 'amount', render: (v: string) => formatUzs(v) },
            { title: t('payments.allocLateFee'), dataIndex: 'isLateFee', render: (v: boolean) => (v ? t('common.yes') : t('common.no')) },
          ]}
        />
      </Card>
    </Space>
  );
}
